import React, { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"
import { useRedeemCouponMutation } from "../../core/api/experience-api"
import CouponCelebration from "./CouponCelebration"

// Auto-redeems the coupon as soon as the route mounts. Shows the celebration
// dialog on success or a friendly error card otherwise.
// Uses a ref guard so React 18's StrictMode (which double-invokes effects in dev)
// doesn't trigger redeem twice.
export default function CouponRedeemPage() {
    const { alias } = useParams()
    const navigate = useNavigate()
    const [redeemCoupon, redeemResp] = useRedeemCouponMutation()
    const [celebrationOpen, setCelebrationOpen] = useState(false)
    const calledRef = useRef(false)

    useEffect(() => {
        if (!alias || calledRef.current) return
        calledRef.current = true
        redeemCoupon(alias).then(resp => {
            if (!resp.error) setCelebrationOpen(true)
        })
    }, [alias, redeemCoupon])

    const errorMessage = redeemResp.error?.data?.error
        || redeemResp.error?.error
        || (redeemResp.error ? "Couldn't redeem the coupon." : null)

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", py: 6, px: 2 }}>
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 4, textAlign: "center" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--xp-orange)" }}>
                    XP coupon
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 1 }}>
                    {alias}
                </Typography>

                {redeemResp.isLoading && (
                    <Stack alignItems="center" spacing={2} sx={{ mt: 4 }}>
                        <CircularProgress />
                        <Typography color="text.secondary">Redeeming…</Typography>
                    </Stack>
                )}

                {redeemResp.isSuccess && !celebrationOpen && (
                    <Stack alignItems="center" spacing={2} sx={{ mt: 4 }}>
                        <Typography color="text.secondary">Coupon redeemed.</Typography>
                        <Button variant="contained" color="warning" onClick={() => navigate("/experience")}>
                            Open My Experience
                        </Button>
                    </Stack>
                )}

                {redeemResp.isError && (
                    <Stack alignItems="center" spacing={2} sx={{ mt: 3 }}>
                        <Alert severity="warning" sx={{ width: "100%" }}>
                            {errorMessage}
                        </Alert>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={() => navigate("/experience")}>
                                My Experience
                            </Button>
                            <Button variant="outlined" onClick={() => navigate("/")}>
                                Shop
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Paper>

            <CouponCelebration
                open={celebrationOpen}
                onClose={() => {
                    setCelebrationOpen(false)
                    navigate("/experience")
                }}
                xpAmount={redeemResp.data?.xpAmount}
                title={redeemResp.data?.title}
                description={redeemResp.data?.description}
            />
        </Box>
    )
}
